import React from 'react';
import styled from '@emotion/styled';
import { Colors, Typography } from '@zapier/design-system';
import { Link } from 'components/Link/Link';
import { SlugTitle } from 'data/SingleArticle/types';
import { useTrackEvent } from 'tracking/useTrackEvent';
import {
  EventAction,
  InteractionName,
  OptionSelected,
  useAvroTracking,
} from 'tracking';

type Props = {
  items: SlugTitle[];
  title: string;
};

export const Pills = ({ items, title }: Props) => {
  const trackEvent = useTrackEvent();
  const { emitUserInteractionEvent } = useAvroTracking();

  return (
    <Outer data-testid={`${title}-pills`}>
      <Heading>{title}</Heading>
      <ContentWrapper>
        {items.map((item) => (
          <PillWrapper key={item.slug}>
            <Link
              href={item.slug}
              onClick={(event) => {
                emitUserInteractionEvent({
                  event_action: EventAction.click,
                  interaction_name: InteractionName.pill_click,
                  interaction_raw_text: item.title,
                  destination_url_address: item.slug,
                  option_selected: OptionSelected.pill_link,
                  custom_tracking_fields: {
                    title,
                  },
                });
                trackEvent('linkClicked', {
                  linkText: item.title,
                  href: item.slug,
                  path: window.location.pathname,
                  posYFromTop: event.clientY,
                });
              }}
            >
              <Content>{item.title}</Content>
            </Link>
          </PillWrapper>
        ))}
      </ContentWrapper>
    </Outer>
  );
};

const Outer = styled.div`
  align-items: baseline;
  display: flex;
  max-width: 600px;
`;

const Heading = styled.strong`
  color: ${Colors.neutral800};
  display: inline;
  float: left;
  margin-right: 16px;
  padding-top: 0;
  text-transform: capitalize;
  ${Typography.paragraph3Bold}
`;

const ContentWrapper = styled.ul`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin: 0;
`;

const PillWrapper = styled.div`
  margin: 0;
`;

const Content = styled.li`
  background-color: ${Colors.neutral200};
  border: 1px solid ${Colors.neutral600};
  border-radius: 30px;
  color: ${Colors.neutral800};
  ${Typography.paragraph3Semibold}
  padding: 10px 20px;

  &:hover {
    background-color: ${Colors.neutral800};
    color: ${Colors.neutral100};
  }
`;
